import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stethoscope, Pencil, Plus, Trash2, Save, X, Loader2, ArrowUpRight } from "lucide-react";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { VendorLogo } from "@/components/vendors/VendorLogo";
import { ContactLines } from "@/components/vendors/VendorLinksContacts";
import { apiError } from "@/components/vendors/vendorUtils";
import { useAuth } from "@/context/AuthContext";

function EntryRow({ entry, idx, vendors, chosen, onChange, onRemove }) {
  const vendor = vendors.find((v) => v.id === entry.vendor_id);
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-center" data-testid={`customer-software-row-${idx}`}>
      <Select value={entry.vendor_id || ""} onValueChange={(v) => onChange({ vendor_id: v, contact_id: null })}>
        <SelectTrigger data-testid={`customer-software-vendor-${idx}`}><SelectValue placeholder="Hersteller wählen" /></SelectTrigger>
        <SelectContent>
          {vendors.filter((v) => v.id === entry.vendor_id || !chosen.includes(v.id)).map((v) => (
            <SelectItem key={v.id} value={v.id}>{v.name}{v.status === "inactive" ? " (inaktiv)" : ""}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={entry.contact_id || "none"} onValueChange={(c) => onChange({ ...entry, contact_id: c === "none" ? null : c })} disabled={!vendor}>
        <SelectTrigger data-testid={`customer-software-contact-${idx}`}><SelectValue placeholder="Ansprechpartner" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Kein Ansprechpartner</SelectItem>
          {(vendor?.contacts || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.role ? ` · ${c.role}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button variant="ghost" size="icon" onClick={onRemove} title="Entfernen" data-testid={`customer-software-remove-${idx}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
    </div>
  );
}

function EntryView({ entry, vendors }) {
  const vendor = vendors.find((v) => v.id === entry.vendor_id);
  if (!vendor) return null;
  const contact = (vendor.contacts || []).find((c) => c.id === entry.contact_id);
  return (
    <div className="flex items-start gap-3 py-2" data-testid={`customer-software-${vendor.id}`}>
      <VendorLogo vendor={vendor} size="w-10 h-10" text="text-sm" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <Link to={`/medical-software/${vendor.id}`} className="font-medium hover:underline inline-flex items-center gap-1" data-testid={`customer-software-link-${vendor.id}`}>
          {vendor.name}<ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
        {contact ? (
          <>
            <p className="text-sm">{contact.name}{contact.role && <span className="text-muted-foreground"> · {contact.role}</span>}</p>
            <ContactLines c={contact} testPrefix={`customer-software-contact-${vendor.id}`} />
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Kein Ansprechpartner ausgewählt</p>
        )}
      </div>
    </div>
  );
}

export function CustomerMedicalSoftwareCard({ customer, onSaved }) {
  const toast = useToast();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const entries = customer.medical_software || [];

  useEffect(() => { vendorsApi.list().then((r) => setVendors(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await vendorsApi.setCustomerSoftware(customer.id, rows.filter((r) => r.vendor_id));
      onSaved(res.data.medical_software);
      setEditing(false);
      toast.success("Arztsoftware gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const chosen = rows.map((r) => r.vendor_id).filter(Boolean);
  return (
    <Card data-testid="customer-software-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Stethoscope className="w-4 h-4" />Arztsoftware</CardTitle>
        {!editing && user?.role !== "readonly" && <Button variant="ghost" size="sm" onClick={() => { setRows(entries.map((e) => ({ ...e }))); setEditing(true); }} data-testid="customer-software-edit"><Pencil className="w-3.5 h-3.5 mr-1.5" />Bearbeiten</Button>}
      </CardHeader>
      <CardContent className="pt-0">
        {editing ? (
          <div className="space-y-3">
            {rows.map((r, i) => (
              <EntryRow key={i} idx={i} entry={r} vendors={vendors} chosen={chosen}
                onChange={(n) => setRows(rows.map((x, j) => (j === i ? n : x)))} onRemove={() => setRows(rows.filter((_, j) => j !== i))} />
            ))}
            <div className="flex flex-wrap justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => setRows([...rows, { vendor_id: "", contact_id: null }])} disabled={chosen.length >= vendors.length} data-testid="customer-software-add"><Plus className="w-4 h-4 mr-1.5" />Hersteller hinzufügen</Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid="customer-software-cancel"><X className="w-4 h-4 mr-1" />Abbrechen</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="customer-software-save">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Speichern</Button>
              </div>
            </div>
          </div>
        ) : entries.length ? (
          <div className="divide-y">{entries.map((e) => <EntryView key={e.vendor_id} entry={e} vendors={vendors} />)}</div>
        ) : (
          <p className="text-sm text-muted-foreground italic" data-testid="customer-software-empty">Keine Arztsoftware zugeordnet.</p>
        )}
      </CardContent>
    </Card>
  );
}
